<template>
  <section class="admin-content" id="encounter-statistics">
    <Navbar />
    <main class="admin-main">

      <!-- Page Header -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white" style="padding: 32px 0;">
            <div class="d-flex align-items-center">
              <div class="header-icon-wrap mr-3">
                <i class="mdi mdi-medical-bag" style="font-size: 22px;"></i>
              </div>
              <div>
                <h4 class="mb-0 font-weight-bold">Encounter Statistics</h4>
                <small style="opacity: 0.8;">Facility-level encounter report and claim conversion</small>
              </div>
            </div>
            <button class="btn filter-toggle-btn" @click="filterOpen = !filterOpen">
              <i class="fe mr-1" :class="filterOpen ? 'fe-x' : 'fe-filter'"></i>
              {{ filterOpen ? 'Close Filter' : 'Filter' }}
            </button>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">

          <!-- Filter Card -->
          <div v-show="filterOpen" class="card shadow-sm form-card mb-4">
            <div class="form-card-header">
              <div class="section-number"><i class="fe fe-filter" style="font-size:12px;"></i></div>
              <h6 class="mb-0 font-weight-bold text-white">Filter Parameters</h6>
            </div>
            <div class="card-body px-4 py-4">
              <div class="form-row">
                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">Health Facility</label>
                  <v-select v-model="selectedProvider" label="agency_name" :options="providers" placeholder="All facilities…" />
                </div>
                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">Start Date</label>
                  <input type="date" class="form-control" v-model="from" />
                </div>
                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">End Date</label>
                  <input type="date" class="form-control" v-model="to" />
                </div>
              </div>
              <div class="d-flex flex-wrap mt-2" style="gap: 8px;">
                <button class="btn btn-submit" @click="load" :disabled="isLoading">
                  <i class="fe fe-bar-chart-2 mr-1"></i> Get Statistics
                </button>
                <button class="btn btn-outline-danger" @click="clearFilters">
                  <i class="fe fe-x-circle mr-1"></i> Clear
                </button>
                <button class="btn btn-outline-secondary ml-md-auto" @click="exportEncounters" :disabled="exporting"
                  title="Every encounter matching these filters, as a CSV file">
                  <span v-if="exporting"><i class="mdi mdi-loading mdi-spin mr-1"></i> Exporting…</span>
                  <span v-else><i class="fe fe-download mr-1"></i> Export CSV</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Summary Cards -->
          <div class="d-flex justify-content-end mb-2">
            <div class="card-download-tools">
              <button class="btn-download" title="Download as image" @click="downloadElementImage('summaryCardsRef', 'encounter-summary')">
                <i class="fe fe-image"></i>
              </button>
              <button class="btn-download" title="Download as CSV" @click="downloadSummaryCsv">
                <i class="fe fe-download"></i>
              </button>
            </div>
          </div>
          <div class="row mb-4" ref="summaryCardsRef">
            <div class="col-6 col-md-4 col-lg-2" v-for="card in summaryCards" :key="card.label">
              <div class="stat-card">
                <div class="stat-icon-wrap" :style="{ background: card.bg }">
                  <i :class="card.icon" :style="{ color: card.color }"></i>
                </div>
                <div>
                  <p class="stat-label">{{ card.label }}</p>
                  <h3 class="stat-value" v-if="card.currency">
                    ₦{{ card.value | numeral('0,0.00') }}
                  </h3>
                  <h3 class="stat-value" v-else>{{ card.value | numeral(0, 0) }}</h3>
                </div>
              </div>
            </div>
          </div>

          <!-- Date Trend Chart -->
          <div class="card shadow-sm table-card mb-4">
            <div class="form-card-header">
              <div class="section-number"><i class="fe fe-trending-up" style="font-size:12px;"></i></div>
              <h6 class="mb-0 font-weight-bold text-white">Encounters by Date</h6>
            </div>
            <div class="card-body p-3">
              <div v-if="isLoading" class="text-center py-4 text-muted">
                <i class="fe fe-loader" style="font-size:1.5rem; opacity:0.4;"></i>
                <p class="mt-2 mb-0 small">Loading chart…</p>
              </div>
              <div v-else-if="!chartSeries[0].data.length" class="text-center py-4 text-muted">
                <i class="fe fe-inbox" style="font-size:2rem; opacity:0.3;"></i>
                <p class="mt-2 mb-0 small">No data — apply filters and click Get Statistics.</p>
              </div>
              <apexchart
                v-else
                type="bar"
                height="280"
                :options="chartOptions"
                :series="chartSeries"
              />
            </div>
          </div>

          <!-- Treatment Type & Reason for Visit -->
          <div class="row mt-2">
            <div class="col-lg-4 mb-4">
              <div class="card shadow-sm table-card h-100">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-pie-chart" style="font-size:12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Treatment Type</h6>
                </div>
                <div class="card-body p-3 d-flex align-items-center justify-content-center">
                  <div v-if="!byTreatmentType.length" class="text-center py-4 text-muted">
                    <i class="fe fe-inbox" style="font-size:1.5rem;opacity:0.3;"></i>
                    <p class="mt-2 mb-0 small">No data for this period.</p>
                  </div>
                  <apexchart v-else type="donut" height="280" :options="treatmentTypeOptions" :series="treatmentTypeSeries" />
                </div>
              </div>
            </div>
            <div class="col-lg-8 mb-4">
              <div class="card shadow-sm table-card h-100">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-map-pin" style="font-size:12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Reason for Visit</h6>
                  <span v-if="byReasonForVisit.length" class="badge badge-light ml-auto" style="border-radius:20px;">{{ byReasonForVisit.length }}</span>
                </div>
                <div class="card-body p-3">
                  <div v-if="!byReasonForVisit.length" class="text-center py-4 text-muted">
                    <i class="fe fe-inbox" style="font-size:1.5rem;opacity:0.3;"></i>
                    <p class="mt-2 mb-0 small">No data for this period.</p>
                  </div>
                  <apexchart v-else type="bar" height="300" :options="barOptions(byReasonForVisit, '#7c3aed', 'reason-for-visit')" :series="barSeries(byReasonForVisit)" />
                </div>
              </div>
            </div>
          </div>

          <!-- Primary Services (from encounters via services_renderreds) -->
          <div class="row mt-2">
            <div class="col-lg-6 mb-4">
              <div class="card shadow-sm table-card h-100">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-package" style="font-size:12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Primary — Drugs Prescribed</h6>
                  <span v-if="primaryServices.drugs.length" class="badge badge-light ml-auto" style="border-radius:20px;">{{ primaryServices.drugs.length }}</span>
                </div>
                <div class="card-body p-3">
                  <div v-if="!primaryServices.drugs.length" class="text-center py-4 text-muted">
                    <i class="fe fe-inbox" style="font-size:1.5rem;opacity:0.3;"></i>
                    <p class="mt-2 mb-0 small">No data for this period.</p>
                  </div>
                  <apexchart v-else type="bar" height="320" :options="barOptions(primaryServices.drugs, '#e11d48', 'primary-drugs')" :series="barSeries(primaryServices.drugs)" />
                </div>
              </div>
            </div>
            <div class="col-lg-6 mb-4">
              <div class="card shadow-sm table-card h-100">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-activity" style="font-size:12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Primary — Services Rendered</h6>
                  <span v-if="primaryServices.services.length" class="badge badge-light ml-auto" style="border-radius:20px;">{{ primaryServices.services.length }}</span>
                </div>
                <div class="card-body p-3">
                  <div v-if="!primaryServices.services.length" class="text-center py-4 text-muted">
                    <i class="fe fe-inbox" style="font-size:1.5rem;opacity:0.3;"></i>
                    <p class="mt-2 mb-0 small">No data for this period.</p>
                  </div>
                  <apexchart v-else type="bar" height="320" :options="barOptions(primaryServices.services, '#2563eb', 'primary-services')" :series="barSeries(primaryServices.services)" />
                </div>
              </div>
            </div>
          </div>

          <!-- Secondary Services (from claims via claims_services) -->
          <div class="row">
            <div class="col-lg-6 mb-4">
              <div class="card shadow-sm table-card h-100">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-package" style="font-size:12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Secondary — Drugs (from Claims)</h6>
                  <span v-if="secondaryServices.drugs.length" class="badge badge-light ml-auto" style="border-radius:20px;">{{ secondaryServices.drugs.length }}</span>
                </div>
                <div class="card-body p-3">
                  <div v-if="!secondaryServices.drugs.length" class="text-center py-4 text-muted">
                    <i class="fe fe-inbox" style="font-size:1.5rem;opacity:0.3;"></i>
                    <p class="mt-2 mb-0 small">No claim drug data for this period.</p>
                  </div>
                  <apexchart v-else type="bar" height="320" :options="barOptions(secondaryServices.drugs, '#d97706', 'secondary-drugs')" :series="barSeries(secondaryServices.drugs)" />
                </div>
              </div>
            </div>
            <div class="col-lg-6 mb-4">
              <div class="card shadow-sm table-card h-100">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-activity" style="font-size:12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Secondary — Services (from Claims)</h6>
                  <span v-if="secondaryServices.services.length" class="badge badge-light ml-auto" style="border-radius:20px;">{{ secondaryServices.services.length }}</span>
                </div>
                <div class="card-body p-3">
                  <div v-if="!secondaryServices.services.length" class="text-center py-4 text-muted">
                    <i class="fe fe-inbox" style="font-size:1.5rem;opacity:0.3;"></i>
                    <p class="mt-2 mb-0 small">No claim service data for this period.</p>
                  </div>
                  <apexchart v-else type="bar" height="320" :options="barOptions(secondaryServices.services, '#059669', 'secondary-services')" :series="barSeries(secondaryServices.services)" />
                </div>
              </div>
            </div>
          </div>

          <!-- Breakdown by Facility & Top Diagnoses -->
          <div class="row mt-2">
            <div class="col-lg-7 mb-4">
              <div class="card shadow-sm table-card h-100">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-list" style="font-size:12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Breakdown by Facility</h6>
                  <span v-if="byProvider.length" class="badge badge-light ml-auto mr-2" style="border-radius:20px;">{{ byProvider.length }} facilities</span>
                  <div class="card-download-tools card-download-tools--dark">
                    <button class="btn-download" title="Download as image" @click="downloadElementImage('byProviderRef', 'breakdown-by-facility')">
                      <i class="fe fe-image"></i>
                    </button>
                    <button class="btn-download" title="Download as CSV"
                      @click="downloadCsv(['Facility', 'Total', 'Claimed', 'Unclaimed'], byProvider.map(function (r) { return [r.provider_name, r.total, r.claimed, r.unclaimed]; }), 'breakdown-by-facility')">
                      <i class="fe fe-download"></i>
                    </button>
                  </div>
                </div>
                <div class="table-responsive" ref="byProviderRef">
                  <table class="table align-td-middle mb-0">
                    <thead style="background:#f8fafc;">
                      <tr>
                        <th class="table-th">#</th>
                        <th class="table-th">Facility</th>
                        <th class="table-th text-center">Total</th>
                        <th class="table-th text-center">Claimed</th>
                        <th class="table-th text-center">Unclaimed</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, i) in byProvider" :key="row.provider_id" class="table-row">
                        <td class="px-4 py-3 text-muted small">{{ i + 1 }}</td>
                        <td class="py-3 font-weight-bold" style="font-size:0.86rem;">{{ row.provider_name }}</td>
                        <td class="py-3 text-center"><span class="badge badge-soft-info" style="border-radius:20px; padding:4px 12px;">{{ row.total | numeral(0, 0) }}</span></td>
                        <td class="py-3 text-center text-success font-weight-bold">{{ row.claimed | numeral(0, 0) }}</td>
                        <td class="py-3 text-center text-warning">{{ row.unclaimed | numeral(0, 0) }}</td>
                      </tr>
                      <tr v-if="!byProvider.length">
                        <td colspan="5" class="text-center py-5 text-muted">
                          <i class="fe fe-inbox" style="font-size:2rem; opacity:0.3;"></i>
                          <p class="mt-2 mb-0">Apply filters and click Get Statistics.</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div class="col-lg-5 mb-4">
              <div class="card shadow-sm table-card h-100">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-thermometer" style="font-size:12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Top Diagnoses</h6>
                  <span v-if="byDiagnosis.length" class="badge badge-light ml-auto mr-2" style="border-radius:20px;">{{ byDiagnosis.length }}</span>
                  <div class="card-download-tools card-download-tools--dark">
                    <button class="btn-download" title="Download as image" @click="downloadElementImage('byDiagnosisRef', 'top-diagnoses')">
                      <i class="fe fe-image"></i>
                    </button>
                    <button class="btn-download" title="Download as CSV"
                      @click="downloadCsv(['Diagnosis', 'Count'], byDiagnosis.map(function (r) { return [r.diagnosis_name, r.total]; }), 'top-diagnoses')">
                      <i class="fe fe-download"></i>
                    </button>
                  </div>
                </div>
                <div class="table-responsive" ref="byDiagnosisRef">
                  <table class="table align-td-middle mb-0">
                    <thead style="background:#f8fafc;">
                      <tr>
                        <th class="table-th">#</th>
                        <th class="table-th">Diagnosis</th>
                        <th class="table-th text-center">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, i) in byDiagnosis" :key="row.diagnosis" class="table-row">
                        <td class="px-4 py-3 text-muted small">{{ i + 1 }}</td>
                        <td class="py-3" style="font-size:0.86rem;">{{ row.diagnosis_name }}</td>
                        <td class="py-3 text-center"><span class="badge badge-soft-info" style="border-radius:20px; padding:4px 12px;">{{ row.total | numeral(0, 0) }}</span></td>
                      </tr>
                      <tr v-if="!byDiagnosis.length">
                        <td colspan="3" class="text-center py-5 text-muted">
                          <i class="fe fe-inbox" style="font-size:1.5rem; opacity:0.3;"></i>
                          <p class="mt-2 mb-0">No data.</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

        </div>

        <div class="vld-parent">
          <loading :active.sync="isLoading" loader="dots" :can-cancel="false" :is-full-page="fullPage" />
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import VueApexCharts from "vue-apexcharts";
import moment from "moment";
moment().format();

function chartToolbar(filename) {
  return {
    show: true,
    tools: { download: true, selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false },
    export: {
      csv: { filename: filename },
      svg: { filename: filename },
      png: { filename: filename },
    },
  };
}

export default {
  components: { Navbar, Loading, apexchart: VueApexCharts },
  data() {
    return {
      isLoading: false,
      fullPage: true,
      filterOpen: false,
      exporting: false,
      providers: [],
      selectedProvider: null,
      from: moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
      to: moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
      summary: { total: 0, claimed: 0, unclaimed: 0, referrals_used: 0, unique_enrollees: 0, multiple_visits: 0, total_claim_amount: 0, total_facilities: 0 },
      byProvider: [],
      byDiagnosis: [],
      byDate: [],
      byTreatmentType: [],
      byReasonForVisit: [],
      primaryServices:   { drugs: [], services: [] },
      secondaryServices: { drugs: [], services: [] },
    };
  },
  computed: {
    summaryCards() {
      return [
        { label: "Total Encounters",   value: this.summary.total,              icon: "mdi mdi-medical-bag",  bg: "#e8f4ff", color: "#2563eb", currency: false },
        { label: "Facilities",          value: this.summary.total_facilities,   icon: "fe fe-home",           bg: "#f0fdf4", color: "#16a34a", currency: false },
        { label: "Claimed",             value: this.summary.claimed,            icon: "fe fe-check-circle",   bg: "#ecfdf5", color: "#059669", currency: false },
        { label: "Unclaimed",           value: this.summary.unclaimed,        icon: "fe fe-clock",          bg: "#fff7e6", color: "#d97706", currency: false },
        { label: "Referrals Used",      value: this.summary.referrals_used || 0, icon: "fe fe-share-2",       bg: "#eff6ff", color: "#3b82f6", currency: false },
        { label: "Unique Enrollees",    value: this.summary.unique_enrollees, icon: "fe fe-users",          bg: "#f5f3ff", color: "#7c3aed", currency: false },
        { label: "Multiple Visits",     value: this.summary.multiple_visits,  icon: "fe fe-repeat",         bg: "#fff1f2", color: "#e11d48", currency: false },
        { label: "Total Claims Amount", value: this.summary.total_claim_amount, icon: "fe fe-credit-card",  bg: "#fefce8", color: "#ca8a04", currency: true  },
      ];
    },
    treatmentTypeOptions() {
      if (!this.byTreatmentType.length) return {};
      return {
        chart: { type: "donut", fontFamily: "inherit", toolbar: chartToolbar("treatment-type") },
        labels: this.byTreatmentType.map(function (d) { return d.name || "Unknown"; }),
        colors: ["#2563eb", "#059669", "#d97706"],
        legend: { position: "bottom" },
        dataLabels: { enabled: true },
        plotOptions: { pie: { donut: { size: "60%" } } },
        tooltip: { y: { formatter: function (v) { return v.toLocaleString() + " visits"; } } },
      };
    },
    treatmentTypeSeries() {
      return this.byTreatmentType.map(function (d) { return Number(d.count || 0); });
    },
    chartOptions() {
      return {
        chart: { type: "bar", toolbar: chartToolbar("encounters-by-date"), fontFamily: "inherit" },
        plotOptions: { bar: { borderRadius: 3, columnWidth: "60%" } },
        dataLabels: { enabled: true },
        xaxis: {
          categories: this.byDate.map((d) => d.date),
          labels: { style: { fontSize: "10px", colors: "#64748b" }, rotate: -30 },
        },
        yaxis: { labels: { style: { fontSize: "11px", colors: "#64748b" } } },
        colors: ["#2563eb", "#059669"],
        legend: { position: "top" },
        grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
        tooltip: { y: { formatter: function (v) { return v.toLocaleString(); } } },
      };
    },
    chartSeries() {
      return [
        { name: "Total",   data: this.byDate.map(function (d) { return Number(d.total   || 0); }) },
        { name: "Claimed", data: this.byDate.map(function (d) { return Number(d.claimed || 0); }) },
      ];
    },
  },
  methods: {
    load() {
      let user = JSON.parse(localStorage.getItem("user") || "{}");
      this.isLoading = true;
      this.axios
        .post("/api/v1/auth/encounter/statistics", {
          agency_id:   user.institutional_id || user.agency_id,
          from:        this.from,
          to:          this.to,
          provider_id: this.selectedProvider ? this.selectedProvider.id : undefined,
        })
        .then(function (res) {
          this.summary           = res.data.summary;
          this.byProvider        = res.data.by_provider;
          this.byDiagnosis       = res.data.by_diagnosis;
          this.byDate            = res.data.by_date;
          this.primaryServices   = res.data.primary_services   || { drugs: [], services: [] };
          this.secondaryServices = res.data.secondary_services || { drugs: [], services: [] };
          this.byTreatmentType   = res.data.by_treatment_type   || [];
          this.byReasonForVisit  = res.data.by_reason_for_visit || [];
          this.$toasted.success("Loaded successfully", { position: "top-center", duration: 2500 });
        }.bind(this))
        .catch(function (err) {
          console.error(err);
          this.$toasted.error("Failed to load statistics", { position: "top-center", duration: 3000 });
        }.bind(this))
        .finally(function () { this.isLoading = false; }.bind(this));
    },
    // Same export as View Encounters: the API builds the CSV (service_summary-agency, format=csv) from this page's filters.
    exportEncounters() {
      let user = JSON.parse(localStorage.getItem("user") || "{}");
      this.exporting = true;
      this.axios
        .post("/api/v1/auth/service_summary-agency", {
          format:      "csv",
          agency_id:   user.institutional_id || user.agency_id,
          from:        this.from,
          to:          this.to,
          provider_id: this.selectedProvider ? this.selectedProvider.id : undefined,
        })
        .then((response) => {
          const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `encounters_${new Date().toISOString().slice(0, 10)}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this.$toasted.success("CSV downloaded successfully", { position: "top-center", duration: 3000 });
        })
        .catch((error) => {
          console.error(error);
          this.$toasted.error("Failed to export CSV", { position: "top-center", duration: 3000 });
        })
        .finally(() => { this.exporting = false; });
    },
    clearFilters() {
      this.from              = moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
      this.to                = moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD");
      this.selectedProvider  = null;
      this.summary           = { total: 0, claimed: 0, unclaimed: 0, unique_enrollees: 0, multiple_visits: 0, total_claim_amount: 0, total_facilities: 0 };
      this.byProvider        = [];
      this.byDiagnosis       = [];
      this.byDate            = [];
      this.byTreatmentType   = [];
      this.byReasonForVisit  = [];
      this.primaryServices   = { drugs: [], services: [] };
      this.secondaryServices = { drugs: [], services: [] };
    },
    fetchProviders() {
      let user = JSON.parse(localStorage.getItem("user") || "{}");
      let agencyId = user.institutional_id || user.agency_id;
      if (!agencyId) { return; }
      this.axios
        .get("/api/v1/auth/providerAgency/" + agencyId)
        .then(function (res) {
          this.providers = (res.data && res.data.data) ? res.data.data : (res.data || []);
        }.bind(this))
        .catch(function (err) { console.error(err); }.bind(this));
    },
    barOptions(data, color, filename) {
      return {
        chart: { type: "bar", toolbar: chartToolbar(filename || "chart"), fontFamily: "inherit" },
        plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "60%" } },
        dataLabels: { enabled: true, style: { fontSize: "11px" } },
        xaxis: {
          categories: data.map(function (d) { return d.name; }),
          labels: { style: { fontSize: "10px", colors: "#64748b" } },
        },
        colors: [color || "#2563eb"],
        grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
        tooltip: { y: { formatter: function (v) { return v.toLocaleString(); } } },
      };
    },
    barSeries(data) {
      return [{ name: "Count", data: data.map(function (d) { return Number(d.count || 0); }) }];
    },
    async downloadElementImage(refName, filename) {
      try {
        const el = this.$refs[refName];
        const dataUrl = await this.$html2canvas(el, { type: "dataURL" });
        const link = document.createElement("a");
        link.href = dataUrl;
        link.setAttribute("download", `${filename}.png`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error(error);
        this.$toasted.error("Could not generate image.", { position: "top-center", duration: 3000 });
      }
    },
    downloadCsv(headers, rows, filename) {
      const escapeCell = function (value) {
        const str = value === null || value === undefined ? "" : String(value);
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
      };
      const lines = [headers, ...rows].map(function (row) { return row.map(escapeCell).join(","); });
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${filename}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    downloadSummaryCsv() {
      const headers = ["Metric", "Value"];
      const rows = this.summaryCards.map(function (c) { return [c.label, c.value]; });
      this.downloadCsv(headers, rows, "encounter-summary");
    },
  },
  created() {
    this.fetchProviders();
    this.load();
  },
};
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, #0f4c35 0%, #059669 100%);
  padding-bottom: 40px;
}

.header-icon-wrap {
  width: 48px; height: 48px;
  background: rgba(255,255,255,0.15);
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}

.filter-toggle-btn {
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.3);
  color: #fff; border-radius: 8px;
  font-weight: 600; font-size: 0.85rem;
  padding: 7px 16px; transition: background 0.15s;
}
.filter-toggle-btn:hover { background: rgba(255,255,255,0.25); color: #fff; }

.stat-card {
  display: flex; align-items: center; gap: 16px;
  background: #fff; border: 1px solid #e9ecef;
  border-radius: 12px; padding: 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  margin-bottom: 16px;
}

.stat-icon-wrap {
  width: 48px; height: 48px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.2rem; flex-shrink: 0;
}

.stat-label {
  font-size: 0.72rem; text-transform: uppercase;
  letter-spacing: 0.05em; color: #94a3b8;
  margin-bottom: 2px; font-weight: 600;
}

.stat-value {
  font-size: 1.35rem; font-weight: 700;
  color: #1e293b; margin-bottom: 0; line-height: 1.2;
}

.form-card, .table-card { border-radius: 12px; border: 1px solid #e9ecef; overflow: hidden; }

.form-card-header {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #0f4c35 0%, #059669 100%);
}

.section-number {
  width: 28px; height: 28px; background: rgba(255,255,255,0.2);
  border-radius: 8px; display: flex; align-items: center; justify-content: center;
  color: #fff; flex-shrink: 0;
}

.table-th {
  font-size: 0.73rem; text-transform: uppercase; letter-spacing: 0.04em;
  color: #64748b; font-weight: 600;
  border-bottom: 2px solid #e2e8f0; padding: 12px 16px;
}

.table-row:hover { background: #f8fafc; }

.btn-submit {
  background: linear-gradient(135deg, #0f4c35 0%, #059669 100%);
  border: none; color: #fff; border-radius: 8px;
  font-weight: 600; padding: 8px 20px;
}

.card-download-tools {
  display: flex; gap: 4px;
}

.card-download-tools--dark {
  margin-left: auto;
}

.btn-download {
  display: flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  border-radius: 6px; border: 1px solid #e2e8f0;
  background: #fff; color: #64748b;
  font-size: 0.8rem; cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.btn-download:hover { background: #f1f5f9; color: #0f4c35; }

.card-download-tools--dark .btn-download {
  border-color: rgba(255,255,255,0.35);
  background: rgba(255,255,255,0.12);
  color: #fff;
}
.card-download-tools--dark .btn-download:hover {
  background: rgba(255,255,255,0.25);
  color: #fff;
}
</style>
